import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ResultsTabs.css';

const tabs = [
  { id: 'brief', label: 'Brief' },
  { id: 'detailed', label: 'Detailed' },
  { id: 'transcript', label: 'Original Transcript' }
];

const ResultsTabs = ({ brief, detailed, transcript }) => {
  const [activeTab, setActiveTab] = useState('brief');

  const getContent = () => {
    switch (activeTab) {
      case 'brief':
        return brief;
      case 'detailed':
        return detailed;
      case 'transcript':
        return transcript;
      default:
        return brief;
    }
  };

  // Parse inline markdown bold (**text**) to JSX
  const parseBold = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const formatContent = (content) => {
    if (!content) return null;

    // Split by newlines and render with proper formatting
    const lines = content.split('\n');

    return lines.map((line, index) => {
      // Roman numeral headers like **I. Introduction** or **II. Personal Preference**
      if (line.match(/^\*\*[IVX]+\.\s.*\*\*$/)) {
        const cleanLine = line.replace(/^\*\*/, '').replace(/\*\*$/, '');
        return <h3 key={index} className="content-header">{cleanLine}</h3>;
      }

      // Headers (lines starting with # or all caps followed by :)
      if (line.match(/^#+\s/) || line.match(/^[A-Z][A-Z\s]+:/)) {
        const cleanLine = line.replace(/^#+\s*/, '');
        return <h3 key={index} className="content-header">{cleanLine}</h3>;
      }

      // Bullet points
      if (line.match(/^[\-•]\s/)) {
        const cleanLine = line.replace(/^[\-•]\s*/, '');
        return <li key={index} className="content-bullet">{parseBold(cleanLine)}</li>;
      }

      // Numbered lists
      if (line.match(/^\d+[\.\)]\s/)) {
        return <li key={index} className="content-numbered">{parseBold(line)}</li>;
      }

      // Empty lines
      if (line.trim() === '') {
        return <br key={index} />;
      }

      // Regular paragraphs with bold parsing
      return <p key={index} className="content-paragraph">{parseBold(line)}</p>;
    });
  };

  return (
    <div className="results-tabs">
      {/* Tab navigation */}
      <div className="tabs-nav">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {activeTab === tab.id && (
              <motion.div
                className="tab-indicator"
                layoutId="tabIndicator"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Content area */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          className="tab-content"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          <div className="content-wrapper">
            {formatContent(getContent())}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ResultsTabs;
